import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  alpha,
  useTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Print, History } from "@mui/icons-material";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { useFoundation } from "../../application/foundation";
import { createTankCargoHistoryService } from "../../application/projections/tankHistory";
import VoyageInformationTable from "./VoyageInformationTable";
import CargoTable from "./CargoTable";
import TankCleaningTable from "./TankCleaningTable";
import TankCoatingTable from "./TankCoatingTable";
import LastThreeCargoesTable from "./LastThreeCargoesTable";
import { getVoyageNumber } from "../../utils/domainSelectors";
import { getVoyageTripsArray } from "../../utils/voyageSelection";

const ThreeLastCargoes = () => {
  const theme = useTheme();
  const { currentVoyage, selectedTripId } = useVoyageContextV2();
  const { shipData, tanks } = useShipSettings();
  const { localDataAccess } = useFoundation();
  const [cleaningList, setCleaningList] = useState([
    { text: "Hot sea water wash", editable: false },
  ]);
  const [tankHistoryRows, setTankHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const tankCargoHistoryService = useMemo(
    () =>
      createTankCargoHistoryService({
        tankRepository: localDataAccess.repositories.tanks,
        voyageRepository: localDataAccess.repositories.voyages,
        tripRepository: localDataAccess.repositories.trips,
        tripCargoRepository: localDataAccess.repositories.tripCargo,
      }),
    [localDataAccess.repositories]
  );

  // Databearbetning för V2 (trips är objekt)
  const tripsArray = currentVoyage ? getVoyageTripsArray(currentVoyage) : [];

  const loadingPorts = [
    ...new Set(
      tripsArray
        .filter((t) => t.operationType === "Loading")
        .map((t) => t.to || t.portName)
        .filter(Boolean)
    ),
  ].join(", ");
  const dischargePorts = [
    ...new Set(
      tripsArray
        .filter((t) => t.operationType === "Discharging")
        .map((t) => t.to || t.portName)
        .filter(Boolean)
    ),
  ].join(", ");
  const loadedCargoes = [
    ...new Set(
      tripsArray
        .filter((t) => t.operationType === "Loading")
        .flatMap((t) => (t.cargos ? Object.values(t.cargos) : []))
        .map((c) => c.cargoName || c.cargo)
        .filter(Boolean)
    ),
  ];

  useEffect(() => {
    let cancelled = false;

    const loadTankHistory = async () => {
      if (!shipData?.id) {
        if (!cancelled) {
          setTankHistoryRows([]);
          setHistoryLoading(false);
        }
        return;
      }

      setHistoryLoading(true);

      try {
        const history = await tankCargoHistoryService.getLastCargoesByTank(
          shipData.id,
          {
            configuredTanks: tanks,
          }
        );

        if (!cancelled) {
          setTankHistoryRows(history);
        }
      } catch (error) {
        console.error("Failed to derive tank cargo history", error);
        if (!cancelled) {
          setTankHistoryRows([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    };

    loadTankHistory();

    return () => {
      cancelled = true;
    };
  }, [currentVoyage?.id, shipData?.id, tankCargoHistoryService, tanks]);

  if (!currentVoyage) return null;

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", pb: 10, px: 3 }}>
      {/* 1. HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4, mt: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="950"
            sx={{ letterSpacing: -1.5 }}
          >
            Cargo History
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Voyage{" "}
            <strong>#{getVoyageNumber(currentVoyage) || "N/A"}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Print />}
          sx={{ borderRadius: 3, px: 4, fontWeight: 900, height: 48 }}
        >
          Generate Report
        </Button>
      </Stack>

      <Grid container spacing={3} alignItems="stretch">
        {/* 2. VOYAGE INFO */}
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <VoyageInformationTable
              currentVoyage={currentVoyage}
              selectedTripId={selectedTripId}
              loadingPorts={loadingPorts}
              dischargePorts={dischargePorts}
            />
          </Paper>
        </Grid>

        {/* 3. STATUS BÄLTE (3-kolumners flex-stretch) */}
        <Grid sx={{ display: "flex" }} size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
              bgcolor: alpha(theme.palette.primary.main, 0.01),
            }}
          >
            <CargoTable loadedCargoes={loadedCargoes} />
          </Paper>
        </Grid>

        <Grid sx={{ display: "flex" }} size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
            }}
          >
            <TankCleaningTable
              cleaningList={cleaningList}
              setCleaningList={setCleaningList}
            />
          </Paper>
        </Grid>

        <Grid sx={{ display: "flex" }} size={{ xs: 12, md: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
            }}
          >
            <TankCoatingTable
              coatingType={currentVoyage.vesselCoating || "Epoxy"}
            />
          </Paper>
        </Grid>

        {/* 4. TANK HISTORY MATRIX */}
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  display: "flex",
                }}
              >
                <History fontSize="small" />
              </Box>
              <Typography variant="h6" fontWeight="950">
                TANK HISTORY MATRIX
              </Typography>
            </Stack>
            {historyLoading ? (
              <Box
                sx={{
                  minHeight: 180,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : (
              <LastThreeCargoesTable cargoes={tankHistoryRows} />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ThreeLastCargoes;
